import axios from "@/axios.js";
import router from "../../router";

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

const state = {
  auth_token: null,
  user: {
    id: null,
    email: null,
    first_name: null,
    last_name: null,
    country: null,
    user_type: null,
    admin: null,
    team_id: null,
    team_name: null,
    team_plan_tier: null,
    team_stripe_cancel_at_period_end: false,
    team_stripe_current_period_end: null,
    team_billing_via_stripe: false,
  },
  login_error: null,
  login_loading: false,
  reset_error: null,
  signup_error: null,
  signup_loading: false,
};

const checkAuthToken = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/validate-token`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      }
    });
    // If the auth token is still valid, return true
    return response.data.valid;
  } catch (error) {
    // If the auth token is invalid or there's an error, return false
    return false;
  }
};

const getters = {
  getAuthToken(state) {
    return state.auth_token;
  },
  getUserEmail(state) {
    return state.user?.email;
  },
  getUserFirst(state) {
    return state.user?.first_name;
  },
  getUserLast(state) {
    return state.user?.last_name;
  },
  getUserID(state) {
    return state.user?.id;
  },
  isAdmin(state) {
    return state.user?.admin === true;
  },
  isLoggedIn(state) {
    const loggedOut =
      state.auth_token == null || state.auth_token == JSON.stringify(null);
    return !loggedOut;
  },
  getSignupError(state) {
    return state.signup_error;
  },
  getSignupLoading(state) {
    return state.signup_loading;
  },
  /** Normalized plan key: free | visibility | intelligence | enterprise (nil/blank team tier → free). */
  teamPlanTierKey(state) {
    const raw = state.user?.team_plan_tier;
    if (raw == null || String(raw).trim() === "") return "free";
    return String(raw).trim().toLowerCase();
  },
  /** Mirrors backend Team#show_intelligence? for standard self-serve tiers. */
  hasIntelligencePlanAccess(state, getters) {
    const t = getters.teamPlanTierKey;
    return t === "intelligence" || t === "enterprise";
  },
  /** Upgrade / paywall CTAs for Action Center blur, etc. (Intelligence tier and above only). */
  lacksIntelligencePlan(state, getters) {
    return !getters.hasIntelligencePlanAccess;
  },
  /** Historical trend charts: Free only; Visibility+ can view trending. */
  lacksHistoricalTrendAccess(state, getters) {
    return getters.teamPlanTierKey === "free";
  },
  /** Free / nil / no-team tier: drives expanded competitor blur on Visibility & Consideration. */
  isFreeTierPlan(state, getters) {
    return getters.teamPlanTierKey === "free";
  },
};
const actions = {
  registerUser({ commit }, payload) {
    commit("setSignupLoading", true);
    commit("setSignupError", null); // Clear any previous errors
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/users`, payload)
        .then((response) => {
          commit("setSignupLoading", false);
          if (response.data.pending_confirmation || !response.headers.authorization) {
            router.push({ name: "ConfirmEmailSent" });
            resolve();
            return;
          }
          commit("setUserInfo", response);
          if (payload.invite && payload.invite.id) {
            router.push({ name: "ClientDashboard" });
          } else {
            router.push({ name: "Dashboard" });
          }
          resolve();
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || "Something went wrong. Please try again.";
          commit("setSignupError", errorMessage);
          commit("setSignupLoading", false);
          // Don't reject to prevent uncaught promise errors
          resolve();
        });
    });
  },
  loginUser({ commit }, payload) {
    commit("setLoginLoading", true);
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/users/sign_in`, payload)
        .then((response) => {
          commit("setUserInfo", response);
          resolve(response);
        })
        .then(() => {
          commit("setLoginLoading", false);
          if (state.user.user_type === "ClientUser") {
            router.push({ name: "ClientDashboard" });
          } else {
            router.push({ name: "Dashboard" });
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            commit("setLoginError", error.response?.data?.message || "Please confirm your email before signing in.");
          } else if (error.response && error.response.status === 401) {
            // Devise's failure app: unconfirmed, awaiting approval, or bad credentials
            commit("setLoginError", error.response?.data?.error || "Incorrect email or password");
          } else if (error.response && error.response.status === 422) {
            commit("setLoginError", "Incorrect email or password");
          }
          commit("setLoginLoading", false);
          // Don't reject the promise to prevent uncaught promise errors
          resolve();
        });
    });
  },
  sendResetEmail({ commit }, payload) {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/passwords/forgot`, payload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            commit("setEmailResetError", "Email not found");
          }
          reject(error);
        });
    });
  },
  async resetPassword({ commit }, payload) {
    await new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/passwords/reset`, payload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            commit("setEmailResetError", error.response.data.error[0]);
          }
          reject(error);
        });
    });
  },
  logoutUser({ commit }) {
    const config = {
      headers: {
        Authorization: localStorage.getItem("auth_token"),
      },
    };
    new Promise((resolve, reject) => {
      axios
        .delete(`${BASE_URL}/users/sign_out`, config)
        .then(() => {
          commit("resetUserInfo");
          resolve();
        })
        .then(() => {
          router.push({ name: "Login" });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async loginUserWithToken({ commit }, payload) {
    const config = {
      headers: {
        Authorization: payload.auth_token,
      },
    };
    await new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/member-data`, config)
        .then((response) => {
          commit("setUserInfoFromToken", response);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async updateUser({ commit }, payload) {
    const config = {
      headers: {
        Authorization: state.auth_token,
      },
    };
    await new Promise((resolve, reject) => {
      axios
        .patch(`${BASE_URL}/profile`, payload, config)
        .then((response) => {
          commit("setUserInfoFromToken", response);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async googleSignUp({ commit }, payload) {
    const data = {
      provider: 'google_oauth2',
      token: payload.credential,
    }
    await new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/google_oauth2/callback`, data)
        .then((response) => {
          commit("setUserInfo", response);
          resolve(response);
        })
        .then(() => {
          if (state.user.user_type === "ClientUser") {
            router.push({ name: "ClientDashboard" });
          } else {
            router.push({ name: "Dashboard" });
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};
const mutations = {
  setUserInfo(state, data) {
    state.user = data.data.user;
    state.auth_token = data.headers.authorization;
    axios.defaults.headers.common["Authorization"] = data.headers.authorization;
    localStorage.setItem("auth_token", data.headers.authorization);
    if (data.data.subscription != null) {
      localStorage.setItem("subscription", data.data.subscription);
    }
  },
  setUserInfoFromToken(state, data) {
    state.user = data.data.user;
    state.auth_token = localStorage.getItem("auth_token");
  },
  resetUserInfo(state) {
    state.user = {
      id: null,
      username: null,
      email: null,
      admin: null,
    };
    state.auth_token = null;
    state.login_loading = false;
    state.login_error = null;
    localStorage.removeItem("auth_token");
    axios.defaults.headers.common["Authorization"] = null;
  },
  setLoginError(state, errorMessage) {
    state.login_error = errorMessage;
  },
  setEmailResetError(state, errorMessage) {
    state.reset_error = errorMessage;
  },
  setLoginLoading(state, loading) {
    state.login_loading = loading;
  },
  setSignupError(state, errorMessage) {
    state.signup_error = errorMessage;
  },
  setSignupLoading(state, loading) {
    state.signup_loading = loading;
  }
};
export default {
  state,
  getters,
  actions,
  mutations,
};